import Store from "../../classes/Store.js";
import Dialog from "../../classes/Dialog.js";
import QRCode from "../../lib/qrcode-svg.js";
import LZString from "lz-string";
import {
    lookupOne,
    empty
} from "../../utils/elements.js";

/**
 * "Spy View" — builds a self-contained, read-only snapshot of the current
 * grimoire (the script selection + placed tokens + pad height), encodes it into
 * a URL fragment, and shows it as a QR code plus an open-in-new-tab link. The
 * snapshot is rendered by processes/spy.js + store.js on the other end.
 *
 * The self-contained `#spy=` URL can be long (the whole grimoire lives in it),
 * which makes for a dense, hard-to-scan QR code. To keep the QR small and
 * reliably scannable we shorten the URL via the urlfy.org API and QR-encode the
 * short link instead. If shortening is unavailable (offline / down / rate
 * limited) we fall back to QR-encoding the long URL directly. The
 * open-in-new-tab link always uses the long self-contained URL so it works
 * instantly and without any third-party dependency.
 */
const store = Store.create("pocket-grimoire");

const button = lookupOne("#spy-view");
const dialog = Dialog.create(lookupOne("#spy-view-dialog"));
const qrHolder = lookupOne("#spy-view-qr");
const openLink = lookupOne("#spy-view-open");
const tooLarge = lookupOne("#spy-view-too-large");
const loading = lookupOne("#spy-view-loading");

const SHORTEN_ENDPOINT = "https://www.urlfy.org/api/v1/shorten";

/** The locale path segment (e.g. "en_GB") of the current page. */
function localeSegment() {
    return window.location.pathname.split("/").filter(Boolean)[0] || "en_GB";
}

/**
 * Compacts the placed tokens to keep the QR payload small (short keys, integer
 * coordinates, and default/false fields omitted). Expanded again by spy.js.
 */
function compactTokens(tokens) {

    return (tokens || []).map((token) => {

        const compact = {
            i: token.id,
            l: Math.round(token.left),
            t: Math.round(token.top),
            z: token.zIndex
        };

        if (token.isDead) { compact.d = 1; }
        if (token.isUpsideDown) { compact.u = 1; }
        if (token.ghostVote) { compact.g = 1; }
        if (token.playerName) { compact.n = token.playerName; }

        return compact;

    });

}

/** Build the shareable Spy View URL from the current store state. */
function buildSpyUrl() {

    const data = store.read();
    // Short top-level keys too (c/k/b/h) — every byte counts for QR density.
    const snapshot = {
        c: data.characters,
        k: compactTokens(data.tokens),
        b: data.bluffs,
        h: data.height
    };
    const encoded = LZString.compressToEncodedURIComponent(JSON.stringify(snapshot));

    return `${window.location.origin}/${localeSegment()}/#spy=${encoded}`;

}

/**
 * Shortens the given URL via the urlfy.org API so the QR code stays small.
 * Resolves to the short URL, or to null if shortening fails for any reason
 * (the caller then falls back to the long URL).
 *
 * @param  {String} longUrl
 *         The self-contained spy URL to shorten.
 * @return {Promise<String|null>}
 *         The short URL, or null on failure.
 */
function shorten(longUrl) {

    return fetch(SHORTEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: longUrl })
    }).then((response) => (
        response.ok
        ? response.json()
        : null
    )).then((result) => (
        result && result.shortUrl
        ? result.shortUrl
        : null
    )).catch(() => null);

}

/**
 * Renders a QR code for the given URL into the holder, falling back to the
 * "too large" message if the URL still exceeds the QR code's capacity.
 *
 * @param {String} url
 *        The URL to encode.
 */
function renderQr(url) {

    empty(qrHolder);

    // ecl "L" maximises capacity (fewer modules); a large render keeps the
    // modules big enough to scan off a screen; pal gives an explicit white
    // background + quiet zone.
    try {
        qrHolder.append(QRCode({ msg: url, ecl: "L", dim: 480, pal: ["#000000", "#ffffff"] }));
        qrHolder.hidden = false;
        tooLarge.hidden = true;
    } catch (ignore) {
        qrHolder.hidden = true;
        tooLarge.hidden = false;
    }

}

button.addEventListener("click", () => {

    const url = buildSpyUrl();

    // The local open-in-new-tab link always uses the self-contained URL.
    openLink.href = url;

    // Show the dialog straight away with a loading state while we shorten.
    empty(qrHolder);
    qrHolder.hidden = true;
    tooLarge.hidden = true;
    loading.hidden = false;
    dialog.show();

    // Shorten for a small QR; fall back to the long URL if it isn't available.
    shorten(url).then((short) => {
        loading.hidden = true;
        renderQr(short || url);
    });

});
